import Logo from "components/Logo";
import SEO from "components/SEO";
import { useEffect, useState, useMemo, useCallback, useRef } from "react";
import dayjs from "dayjs";
import { useParams, useLocation, Navigate } from "react-router-dom";
import {
  getValidUrl,
  getSafeHref,
  getAccessibleTextColor,
  getSecondaryTextColor,
  toCssHex,
} from "utils/helpers";
import { capture } from "utils/posthog";
import { useGetGallery, useGetChannelInfo } from "./api/queries";
import { clearUnlockToken } from "./api/mutations";
import GalleryFooter from "./Components/GalleryFooter";
import GalleryList from "./Components/GalleryList";
import GalleryLoader from "./Components/GalleryLoader";
import GallerySkeleton from "./Components/GallerySkeleton";
import GalleryLoadError from "./Components/GalleryLoadError";
import GalleryToolbar from "./Components/GalleryToolbar";
import SingleViewModal from "./Components/SingleViewModal";
import GalleryGate from "./Components/GalleryGate";
import GalleryExpired, { isGalleryExpired } from "./Components/GalleryExpired";

/**
 * Default "oldest" date for date range filters.
 * Set to a date well before Movebooth's founding (2010s) to ensure
 * all historical content is included when no specific date filter is applied.
 * This effectively means "from the beginning" for practical purposes.
 */
const DEFAULT_OLDEST_DATE = '2000-01-01';

function GalleryPage() {
  const { slug } = useParams();
  const location = useLocation();
  const [page, setPage] = useState(0);
  const [singleOpen, setSingleOpen] = useState(false);
  const [filterPage, setFilterPage] = useState(0);
  const [gifs, setGifs] = useState([]);
  const [filters, setFilters] = useState({ start: null, end: null });
  const [filteredGifs, setFilteredGifs] = useState([]);
  const [firstLoad, setFirstLoad] = useState(true);
  const [channelData, setChannelData] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [scrolled, setScrolled] = useState(false);
  const [totalCount, setTotalCount] = useState(null);

  // Hide Intercom widget on gallery pages
  useEffect(() => {
    if (window.Intercom) {
      window.Intercom('update', { hide_default_launcher: true });
    }
    return () => {
      // Show Intercom widget again when leaving gallery page
      if (window.Intercom) {
        window.Intercom('update', { hide_default_launcher: false });
      }
    };
  }, []);
  // Compute default date range for "All Time" downloads
  // Uses conservative start date and today as end to capture all possible gallery images
  const computedDateRange = useMemo(() => {
    return {
      oldest: DEFAULT_OLDEST_DATE,
      newest: dayjs().format('YYYY-MM-DD')  // Today's date
    };
  }, []);

  const hasFilter = filters.start !== null;

  const { data, error, isLoading, refetch } = useGetGallery({
    slug,
    params: {
      page: hasFilter ? filterPage : page,
      start: hasFilter ? filters.start : undefined,
      end: hasFilter ? filters.end : undefined,
      big: true
    },
  });

  const channel = data?.channel || error?.channel;

  // rails answers a locked gallery with `401 { is_protected: true }` and no
  // channel, so the password gate reads the channel's public details (colour,
  // logo, headline) from the gif-free channel endpoint, as the shared-photo
  // page does. The password protects gallery content, not channel config.
  const isProtected = !!error?.is_protected;
  const { data: gateChannel, isInitialLoading: gateChannelLoading } =
    useGetChannelInfo(isProtected ? slug : undefined);

  // Check if URL uses numeric channel ID (used for redirect logic below)
  const isNumericId = /^\d+$/.test(slug);

  // Product measurement: one event per gallery visit, keyed by slug only —
  // no gif/photo content, no viewer identity. `capture` buffers until the
  // deferred PostHog init lands (see utils/thirdParty) and no-ops keyless.
  //
  // A numeric /ch/:id URL redirects to the canonical /ch/:slug (see the
  // <Navigate> below), which remounts this page and would otherwise count one
  // visit twice, split across two identifiers. So a numeric route holds its
  // capture until channel data proves no redirect is coming (the channel's
  // canonical slug IS the numeric param); when a redirect fires, only the
  // post-redirect mount captures, under the canonical slug. The ref keeps the
  // effect single-shot per slug even as the channel dependency settles.
  const viewCapturedForSlug = useRef(null);
  useEffect(() => {
    if (!slug || viewCapturedForSlug.current === slug) return;
    if (isNumericId && (!channel?.slug || channel.slug !== slug)) return;
    viewCapturedForSlug.current = slug;
    capture("gallery_viewed", { channel_slug: slug });
  }, [slug, isNumericId, channel]);

  // Throttled scroll handler for toolbar show/hide
  useEffect(() => {
    const scrollableDiv = document.getElementById("scrollableDiv");
    if (!scrollableDiv) return;

    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(() => {
          const shouldBeScrolled = scrollableDiv.scrollTop > 200;
          setScrolled((prev) => (prev !== shouldBeScrolled ? shouldBeScrolled : prev));
          ticking = false;
        });
      }
    };

    scrollableDiv.addEventListener("scroll", handleScroll, { passive: true });
    return () => scrollableDiv.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (!channelData && !!channel && firstLoad) {
      setChannelData(channel);
      setFirstLoad(false);
    }
  }, [channel, channelData, firstLoad]);

  // Clear stale unlock token when a protected-gallery error arrives.
  // Must be a useEffect — clearUnlockToken mutates localStorage and cannot
  // be called during render.
  useEffect(() => {
    if (error?.is_protected) {
      clearUnlockToken(slug);
    }
  }, [error?.is_protected, slug]);

  // Capture total count only on first successful load (don't update on pagination)
  useEffect(() => {
    if (totalCount === null && data?.total_count > 0) {
      setTotalCount(data.total_count);
    }
  }, [data?.total_count, totalCount]);

  // Accumulate gifs from paginated API responses using functional updates
  useEffect(() => {
    if (!data?.gifs) return;
    if (hasFilter) {
      if (filterPage === 0) {
        setFilteredGifs(data.gifs);
      } else {
        setFilteredGifs((prev) => [...prev, ...data.gifs]);
      }
    } else {
      if (page === 0) {
        setGifs(data.gifs);
        // Increment page so the next data set is automatically loaded
        // to prevent the virtualizer from having too few items on large screens
        setPage(1);
      } else {
        setGifs((prev) => [...prev, ...data.gifs]);
      }
    }
  }, [data, hasFilter, filterPage, page]);

  const loadMore = useCallback(() => {
    if (hasFilter) {
      setFilterPage((p) => p + 1);
    } else {
      setPage((p) => p + 1);
    }
  }, [hasFilter]);

  const handleOpenSingleView = useCallback((index) => {
    setSelectedIndex(index);
    setSingleOpen(true);
  }, []);

  const header = channelData?.header;
  const subHeader = channelData?.sub_header;
  const logoImage =
    getValidUrl(channelData?.gallery_banner) ||
    getValidUrl(channelData?.big_logo_image_url);

  const hasMore = isLoading ? true : (data?.has_more ?? (data?.gifs?.length > 0));

  const handleFilterReset = () => {
    setFilteredGifs([]);
    setFilters({ start: null, end: null });
    setPage(0);
  };

  const handleFilterSubmit = (data) => {
    setFilterPage(0);
    setFilters({ start: data.startDate, end: data.endDate });
    setGifs([]);
  };

  // Scheme-guard the owner-supplied CTA link before it reaches an `href`.
  // A stored `javascript:`/`data:` URL would otherwise execute on a public
  // gallery visitor's click (stored DOM-XSS).
  const ctaHref = getSafeHref(channelData?.gallery_cta_link);

  // The first load failed with no channel data (password and expiry are
  // handled before the page renders).
  const loadFailed = !channelData && !!error;

  const backgroundColor = toCssHex(
    channelData?.gallery_color || channelData?.app_background_color,
    "#000000"
  );
  // Unset headline/subheadline colours follow the gallery colour, so a light
  // gallery gets dark text instead of white-on-white. A colour the channel
  // owner chose is always used as-is.
  const textColor = getAccessibleTextColor(backgroundColor);
  const headerColor = toCssHex(channelData?.header_color, textColor);
  const subHeaderColor = toCssHex(
    channelData?.sub_header_color,
    getSecondaryTextColor(backgroundColor)
  );

  // Redirect numeric IDs to canonical slug URL
  // IMPORTANT: Preserve query strings and hash for analytics tracking
  // This must come after all hooks but before the main render
  if (isNumericId && channel?.slug && channel.slug !== slug) {
    return (
      <Navigate
        to={`/ch/${channel.slug}${location.search}${location.hash || ""}`}
        replace
      />
    );
  }

  if (isGalleryExpired(error)) {
    return <GalleryExpired />;
  }

  if (isProtected) {
    // Hold the gate until the channel details land (or fail), so it opens in
    // the gallery's colour with its logo rather than flashing the generic one.
    if (gateChannelLoading) {
      return <div className="h-screen w-screen bg-black" aria-busy="true" />;
    }
    const gate = { ...gateChannel, ...channel };
    const gateBackgroundColor = toCssHex(
      gate.gallery_color || gate.app_background_color,
      "#000000"
    );
    return (
      <GalleryGate
        channelId={slug}
        galleryColor={gateBackgroundColor}
        logoUrl={
          getValidUrl(gate.gallery_banner) ||
          getValidUrl(gate.big_logo_image_url)
        }
        headline={gate.header}
        headlineColor={toCssHex(gate.header_color, undefined)}
      />
    );
  }

  return (
    <>
      <SingleViewModal
        open={singleOpen}
        setOpen={setSingleOpen}
        selectedIndex={selectedIndex}
        channel={channelData}
        allGifs={hasFilter ? filteredGifs : gifs}
        backgroundColor={backgroundColor}
        channelSlug={slug}
      />
      {!channelData && !firstLoad ? (
        <div className="w-full h-full flex justify-center items-center">
          <span className="text-2xl text-mb-dune">
            Cannot get channel detail
          </span>
        </div>
      ) : (
        <>
          <div
            className="w-full h-full overflow-auto"
            id="scrollableDiv"
            style={{
              background: backgroundColor,
            }}
          >
            <>
              {loadFailed ? null : (
              <GalleryToolbar
                visible={scrolled}
                filters={filters}
                backgroundColor={backgroundColor}
                filterSubmit={handleFilterSubmit}
                filterReset={handleFilterReset}
                selectedIndex={selectedIndex}
                channel={channelData}
                computedDateRange={computedDateRange}
                logo={
                  // Narrower under sm, so the toolbar and its date filter
                  // fit a 360px phone without scrolling sideways.
                  <div className="min-w-0 max-w-[120px] sm:max-w-[184px] max-h-[40px] [&_svg]:w-full [&_svg]:h-auto">
                    {logoImage ? (
                      <img src={logoImage} alt="logo" className="max-w-full max-h-[40px] w-auto h-auto object-contain" />
                    ) : (
                      <Logo withText={true} fill={textColor} />
                    )}
                  </div>
                }
              />
              )}
              <SEO
                title={
                  channelData?.name
                    ? /photo\s*booth/i.test(channelData.name)
                      ? `${channelData.name} Gallery`
                      : `${channelData.name} Photo Booth Gallery`
                    : "Photo Booth Gallery"
                }
                description={
                  channelData?.header ||
                  (channelData?.name
                    ? (/photo\s*booth/i.test(channelData.name)
                        ? `Browse moments from ${channelData.name}. Download photos and GIFs - powered by Movebooth.`
                        : `Browse photo booth moments from ${channelData.name}. Download photos and GIFs - powered by Movebooth.`)
                    : "Browse photo booth moments from this event gallery. Download photos and GIFs - powered by Movebooth.")
                }
                canonical={`${window.location.origin}/ch/${slug}`}
                ogType="website"
                ogImage={logoImage}
              />
              <div className="h-full">
                {!channelData && firstLoad && !error ? (
                  <GallerySkeleton />
                ) : loadFailed ? (
                  // The first load failed (not a password or an expired
                  // gallery, handled above): a designed message, with the
                  // footer and its legal links still below.
                  <GalleryLoadError
                    notFound={error?.statusCode === 404}
                    onRetry={() => refetch()}
                  />
                ) : (
                  <div
                    style={{
                      // Clears the fixed 80px footer, so the last row of
                      // photos (or an empty gallery's message) can scroll
                      // fully above it.
                      paddingBottom: "96px",
                      position: "relative",
                    }}
                  >
                    {/* One spacing scale for the hero: 32px from the logo to
                        the message, 24px from the text to the CTA, 16px between headline and
                        subheadline. */}
                    <div className="flex flex-col items-center px-4 pt-20 pb-12 sm:px-6 sm:pt-24 sm:pb-16 w-full">
                      <div className="flex flex-col gap-8 items-center w-full max-w-[640px]">
                        <div
                          className={`w-full max-w-[480px] transition-all ${
                            scrolled ? "opacity-0" : "opacity-100"
                          }`}
                        >
                          {logoImage ? (
                            <img
                              src={logoImage}
                              alt="logo"
                              className="w-full h-auto object-contain"
                            />
                          ) : (
                            <Logo withText={true} fill={textColor} />
                          )}
                        </div>
                        {header || subHeader || ctaHref ? (
                        // The CTA sits 24px under its text, closer than the
                        // 32px logo gap, so it reads as part of the message.
                        <div className="flex flex-col gap-6 items-center w-full">
                        {header || subHeader ? (
                          <div className="flex flex-col gap-4 items-center text-center w-full">
                            {header ? (
                              // text-balance evens the lines, so a long
                              // headline never leaves one word alone on the
                              // last line; the smaller phone size keeps the
                              // measure from collapsing to a word per line.
                              <h1
                                className="text-4xl sm:text-5xl font-headline font-bold leading-[1.1] text-balance break-words w-full"
                                style={{ color: headerColor }}
                              >
                                {header}
                              </h1>
                            ) : null}
                            {subHeader ? (
                              <p
                                className="text-lg sm:text-xl font-headline font-normal leading-normal text-balance break-words w-full max-w-[34rem]"
                                style={{ color: subHeaderColor }}
                              >
                                {subHeader}
                              </p>
                            ) : null}
                          </div>
                        ) : null}
                        {ctaHref ? (
                          // A solid fill in the gallery's accessible text
                          // colour, labelled in the gallery colour: the same
                          // AA pair as the headline, reversed, so it never
                          // reads as disabled on a light or dark gallery.
                          // Hover draws a thin outline instead of fading, so
                          // the label keeps its full contrast.
                          <a
                            target="_blank"
                            className="group inline-flex items-center justify-center gap-2 max-w-full min-h-[48px] px-6 py-3 rounded-lg text-center text-balance font-headline text-base font-bold leading-tight transition-transform duration-200 [transition-timing-function:var(--ease-out)] hover:outline hover:outline-1 hover:outline-offset-2 active:scale-[0.98] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4"
                            href={ctaHref}
                            rel="noreferrer"
                            style={{
                              background: textColor,
                              color: backgroundColor,
                              outlineColor: textColor,
                            }}
                          >
                            {/* The arrow rides inline after the label, joined
                                to its last word by a no-break space, so a
                                wrapped label keeps the arrow beside it. */}
                            <span className="min-w-0 break-words">
                              {channelData?.gallery_cta_text}
                              <span className="whitespace-nowrap">
                                &nbsp;
                                <svg
                                  width="16"
                                  height="16"
                                  viewBox="0 0 16 16"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                  aria-hidden="true"
                                  className="inline-block align-[-0.15em] ml-1 transition-transform duration-200 [transition-timing-function:var(--ease-out)] group-hover:translate-x-0.5"
                                >
                                  <path
                                    d="M3.33334 8H12.6667M12.6667 8L8.00001 3.33334M12.6667 8L8.00001 12.6667"
                                    stroke="currentColor"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                </svg>
                              </span>
                            </span>
                          </a>
                        ) : null}
                        </div>
                        ) : null}
                      </div>
                    </div>
                    <GalleryList
                      slug={slug}
                      gifs={hasFilter ? filteredGifs : gifs}
                      setGifs={hasFilter ? setFilteredGifs : setGifs}
                      error={error}
                      onImageClick={handleOpenSingleView}
                      loading={isLoading}
                      onLoadMore={loadMore}
                      hasMore={hasMore}
                      emptyTextColor={getSecondaryTextColor(backgroundColor)}
                    />
                    {isLoading && (hasFilter ? filteredGifs : gifs).length > 0 && (
                      <GalleryLoader />
                    )}
                  </div>
                )}
              </div>
            </>
          </div>
          <GalleryFooter slug={slug} totalCount={totalCount} />
        </>
      )}
    </>
  );
}

export default GalleryPage;
